import { Injectable, OnDestroy } from '@angular/core';

/**
 * Phase 6: Cross-Tab Synchronization Service
 *
 * Coordinates activity tracking, logout events, and session state across multiple browser tabs.
 * Uses BroadcastChannel API for efficient cross-tab communication.
 *
 * Features:
 * - Activity event broadcasting (resets timers in all tabs when user active in any tab)
 * - Logout event broadcasting (all tabs logout when one tab initiates logout)
 * - Tab counting (tracks how many tabs are open)
 * - Warning modal synchronization (show/dismiss across all tabs)
 */

export type CrossTabMessageType = 'activity' | 'logout' | 'tab-opened' | 'tab-closed' | 'ping' | 'pong' | 'warning-show' | 'warning-dismiss';

export interface CrossTabMessage {
  type: CrossTabMessageType;
  timestamp: number;
  tabId: string;
  data?: any;
}

@Injectable({
  providedIn: 'root'
})
export class CrossTabSyncService implements OnDestroy {
  private readonly CHANNEL_NAME = 'ascend-cross-tab-sync';
  private readonly TAB_COUNT_KEY = 'ascend-tab-count';
  private readonly TAB_ID_PREFIX = 'ascend-tab-';

  private channel: BroadcastChannel | null = null;
  private tabId: string;
  private activityCallbacks: Array<() => void> = [];
  private logoutCallbacks: Array<() => void> = [];
  private warningShowCallbacks: Array<() => void> = [];
  private warningDismissCallbacks: Array<() => void> = [];
  private isInitialized = false;

  constructor() {
    this.tabId = this.generateTabId();
    this.initializeBroadcastChannel();
    this.registerTab();
    this.setupBeforeUnloadHandler();
  }

  /**
   * Generate unique ID for this tab
   */
  private generateTabId(): string {
    return `${this.TAB_ID_PREFIX}${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Initialize BroadcastChannel for cross-tab communication
   */
  private initializeBroadcastChannel(): void {
    if (typeof BroadcastChannel === 'undefined') {
      console.warn('[CrossTabSync] BroadcastChannel not supported, falling back to localStorage');
      return;
    }

    try {
      this.channel = new BroadcastChannel(this.CHANNEL_NAME);
      this.channel.onmessage = (event) => this.handleMessage(event.data);
      this.isInitialized = true;
      console.log(`[CrossTabSync] Initialized for tab ${this.tabId}`);
    } catch (error) {
      console.error('[CrossTabSync] Failed to initialize BroadcastChannel:', error);
    }
  }

  /**
   * Register this tab in the tab count
   */
  private registerTab(): void {
    const count = this.getTabCount();
    this.setTabCount(count + 1);
    console.log(`[CrossTabSync] Tab registered. Total tabs: ${count + 1}`);

    // Broadcast tab-opened event
    this.broadcastMessage({
      type: 'tab-opened',
      timestamp: Date.now(),
      tabId: this.tabId
    });
  }

  /**
   * Unregister this tab from the tab count
   */
  private unregisterTab(): void {
    const count = this.getTabCount();
    if (count > 0) {
      this.setTabCount(count - 1);
      console.log(`[CrossTabSync] Tab unregistered. Remaining tabs: ${count - 1}`);
    }

    // Broadcast tab-closed event
    this.broadcastMessage({
      type: 'tab-closed',
      timestamp: Date.now(),
      tabId: this.tabId
    });
  }

  /**
   * Setup beforeunload handler to unregister tab
   */
  private setupBeforeUnloadHandler(): void {
    window.addEventListener('beforeunload', () => {
      this.unregisterTab();
    });
  }

  /**
   * Get current tab count from localStorage
   */
  private getTabCount(): number {
    try {
      const count = localStorage.getItem(this.TAB_COUNT_KEY);
      return count ? parseInt(count, 10) : 0;
    } catch (error) {
      console.error('[CrossTabSync] Failed to get tab count:', error);
      return 0;
    }
  }

  /**
   * Set tab count in localStorage
   */
  private setTabCount(count: number): void {
    try {
      localStorage.setItem(this.TAB_COUNT_KEY, count.toString());
    } catch (error) {
      console.error('[CrossTabSync] Failed to set tab count:', error);
    }
  }

  /**
   * Broadcast message to all other tabs
   */
  private broadcastMessage(message: CrossTabMessage): void {
    if (this.channel) {
      try {
        this.channel.postMessage(message);
      } catch (error) {
        console.error('[CrossTabSync] Failed to broadcast message:', error);
      }
    }
  }

  /**
   * Handle incoming messages from other tabs
   */
  private handleMessage(message: CrossTabMessage): void {
    // Ignore messages from this tab
    if (message.tabId === this.tabId) {
      return;
    }

    console.log(`[CrossTabSync] Received ${message.type} from tab ${message.tabId}`);

    switch (message.type) {
      case 'activity':
        // User is active in another tab - reset timers in this tab
        this.activityCallbacks.forEach(callback => callback());
        break;

      case 'logout':
        // Another tab initiated logout - logout this tab too
        this.logoutCallbacks.forEach(callback => callback());
        break;

      case 'warning-show':
        // Warning triggered in another tab - show warning in this tab too
        this.warningShowCallbacks.forEach(callback => callback());
        break;

      case 'warning-dismiss':
        // Warning dismissed in another tab (user is active) - dismiss in this tab too
        this.warningDismissCallbacks.forEach(callback => callback());
        break;

      case 'tab-opened':
        // Another tab opened - update tab count
        console.log('[CrossTabSync] Another tab opened');
        break;

      case 'tab-closed':
        // Another tab closed - update tab count
        console.log('[CrossTabSync] Another tab closed');
        break;

      default:
        console.warn('[CrossTabSync] Unknown message type:', message.type);
    }
  }

  /**
   * Broadcast activity event to all tabs
   * Called when user is active in this tab
   */
  public broadcastActivity(): void {
    this.broadcastMessage({
      type: 'activity',
      timestamp: Date.now(),
      tabId: this.tabId
    });
  }

  /**
   * Broadcast logout event to all tabs
   * Called when user logs out in this tab
   */
  public broadcastLogout(): void {
    this.broadcastMessage({
      type: 'logout',
      timestamp: Date.now(),
      tabId: this.tabId
    });
  }

  /**
   * Register callback for activity events from other tabs
   */
  public onActivity(callback: () => void): void {
    this.activityCallbacks.push(callback);
  }

  /**
   * Register callback for logout events from other tabs
   */
  public onLogout(callback: () => void): void {
    this.logoutCallbacks.push(callback);
  }

  /**
   * Broadcast warning show event to all tabs
   * Called when warning threshold is reached
   */
  public broadcastWarningShow(): void {
    this.broadcastMessage({
      type: 'warning-show',
      timestamp: Date.now(),
      tabId: this.tabId
    });
  }

  /**
   * Register callback for warning show events from other tabs
   */
  public onWarningShow(callback: () => void): void {
    this.warningShowCallbacks.push(callback);
  }

  /**
   * Broadcast warning dismiss event to all tabs
   * Called when user activity dismisses the warning modal
   */
  public broadcastWarningDismiss(): void {
    this.broadcastMessage({
      type: 'warning-dismiss',
      timestamp: Date.now(),
      tabId: this.tabId
    });
  }

  /**
   * Register callback for warning dismiss events from other tabs
   */
  public onWarningDismiss(callback: () => void): void {
    this.warningDismissCallbacks.push(callback);
  }

  /**
   * Check if this is the last tab
   */
  public isLastTab(): boolean {
    const count = this.getTabCount();
    // Count will be 1 when this is the last tab (this tab is still registered)
    return count <= 1;
  }

  /**
   * Get current tab ID
   */
  public getTabId(): string {
    return this.tabId;
  }

  /**
   * Check if service is initialized
   */
  public isServiceInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Cleanup on service destroy
   */
  ngOnDestroy(): void {
    this.unregisterTab();
    if (this.channel) {
      this.channel.close();
    }
    this.activityCallbacks = [];
    this.logoutCallbacks = [];
    this.warningShowCallbacks = [];
    this.warningDismissCallbacks = [];
  }
}
